<template>
	<div
		class="p-3 sm:p-6 bg-white backdrop-blur-sm rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in-up"
	>
		<!-- Header Section -->
		<div class="mb-4 sm:mb-6 text-center">
			<h2 class="text-xl sm:text-3xl font-bold text-gray-800">
				Application Summary
			</h2>
			<p class="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-500">
				Review your details before proceeding to payment
			</p>
			<div
				class="mt-2 sm:mt-4 w-12 sm:w-16 h-0.5 sm:h-1 bg-primary rounded-full mx-auto"
			></div>
		</div>

		<div class="space-y-4 sm:space-y-6">
			<!-- Insurance Details Card -->
			<div
				class="p-3 sm:p-5 bg-white rounded-lg border border-gray-100 shadow-sm"
			>
				<div class="flex items-center mb-3 sm:mb-4">
					<div
						class="p-1.5 sm:p-2 bg-blue-100 rounded-lg mr-2 sm:mr-3"
					>
						<i class="fas fa-shield-alt text-primary"></i>
					</div>
					<h3
						class="text-base sm:text-lg font-semibold text-gray-800"
					>
						Insurance Details
					</h3>
				</div>

				<div class="grid grid-cols-2 gap-2 sm:gap-4">
					<div class="p-2 sm:p-3 bg-gray-50 rounded-lg">
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Cover Type
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{ selectedCoverType.name || 'N/A' }}
						</p>
					</div>

					<div
						v-if="selectedCoverType !== 'third-party'"
						class="p-2 sm:p-3 bg-gray-50 rounded-lg"
					>
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Vehicle Value
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							KES
							{{
								quotation && quotation.vehicleValue
									? quotation.vehicleValue
									: vehicleValue
							}}
						</p>
					</div>

					<div class="p-2 sm:p-3 bg-gray-50 rounded-lg">
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Product
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{
								(selectedProduct &&
									selectedProduct.productName) ||
								'N/A'
							}}
						</p>
					</div>

					<div class="p-2 sm:p-3 bg-gray-50 rounded-lg">
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Premium
						</p>
						<p
							class="text-sm font-semibold text-primary mt-0.5 sm:mt-1"
						>
							KES
							{{
								selectCalculatedQuote &&
								selectCalculatedQuote.totalPremium != null
									? Math.round(
											selectCalculatedQuote.totalPremium,
										)
									: 0
							}}
						</p>
					</div>

					<div class="p-2 sm:p-3 bg-gray-50 rounded-lg">
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Cover Period
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{
								quotation && quotation.period
									? `${quotation.period} Days`
									: 'N/A'
							}}
						</p>
					</div>

					<div class="p-2 sm:p-3 bg-gray-50 rounded-lg">
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Start Date
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{ (quotation && quotation.start_date) || 'N/A' }}
						</p>
					</div>
				</div>
			</div>

			<!-- Vehicle Details Card -->
			<div
				class="p-3 sm:p-5 bg-white rounded-lg border border-gray-100 shadow-sm"
			>
				<div class="flex items-center mb-3 sm:mb-4">
					<div
						class="p-1.5 sm:p-2 bg-teal-100 rounded-lg mr-2 sm:mr-3"
					>
						<i class="fas fa-check text-brand-teal"></i>
					</div>
					<h3
						class="text-base sm:text-lg font-semibold text-gray-800"
					>
						Vehicle Details
					</h3>
				</div>

				<div class="grid grid-cols-2 gap-2 sm:gap-4">
					<div class="p-2 sm:p-3 bg-gray-50 rounded-lg">
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Make
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{
								(vehicleDetails && vehicleDetails.make) || 'N/A'
							}}
						</p>
					</div>

					<div class="p-2 sm:p-3 bg-gray-50 rounded-lg">
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Model
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{
								(vehicleDetails && vehicleDetails.model) ||
								'N/A'
							}}
						</p>
					</div>

					<div class="p-2 sm:p-3 bg-gray-50 rounded-lg">
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Registration Number
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{
								(vehicleDetails &&
									vehicleDetails.registrationNumber) ||
								'N/A'
							}}
						</p>
					</div>

					<div class="p-2 sm:p-3 bg-gray-50 rounded-lg">
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Chassis Number
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{
								(vehicleDetails &&
									vehicleDetails.chassisNumber) ||
								'N/A'
							}}
						</p>
					</div>

					<div class="p-2 sm:p-3 bg-gray-50 rounded-lg">
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Engine Number
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{
								(vehicleDetails &&
									vehicleDetails.engineNumber) ||
								'N/A'
							}}
						</p>
					</div>

					<div class="p-2 sm:p-3 bg-gray-50 rounded-lg">
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Engine Capacity
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{
								(vehicleDetails &&
									vehicleDetails.engineCapacity) ||
								'N/A'
							}}
						</p>
					</div>

					<div class="p-2 sm:p-3 bg-gray-50 rounded-lg">
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Body Type
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{
								(vehicleDetails && vehicleDetails.bodyType) ||
								'N/A'
							}}
						</p>
					</div>

					<div class="p-2 sm:p-3 bg-gray-50 rounded-lg">
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Fuel Type
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{
								(vehicleDetails && vehicleDetails.fuelType) ||
								'N/A'
							}}
						</p>
					</div>

					<div class="p-2 sm:p-3 bg-gray-50 rounded-lg">
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Anti-Theft Device
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{
								vehicleDetails &&
								vehicleDetails.antiTheftDevice === 'yes'
									? 'Yes'
									: 'No'
							}}
						</p>
					</div>

					<div
						v-if="
							vehicleDetails &&
							vehicleDetails.antiTheftDevice === 'yes'
						"
						class="p-2 sm:p-3 bg-gray-50 rounded-lg"
					>
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Anti-Theft Device Details
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{
								(vehicleDetails &&
									vehicleDetails.antiTheftDetails) ||
								'N/A'
							}}
						</p>
					</div>

					<div class="p-2 sm:p-3 bg-gray-50 rounded-lg">
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Modifications
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{
								vehicleDetails &&
								vehicleDetails.modifications === 'yes'
									? 'Yes'
									: 'No'
							}}
						</p>
					</div>

					<div
						v-if="
							vehicleDetails &&
							vehicleDetails.modifications === 'yes'
						"
						class="p-2 sm:p-3 bg-gray-50 rounded-lg"
					>
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Modification Details
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{
								(vehicleDetails &&
									vehicleDetails.modificationDetails) ||
								'N/A'
							}}
						</p>
					</div>

					<div
						v-if="
							vehicleDetails &&
							vehicleDetails.modifications === 'yes'
						"
						class="p-2 sm:p-3 bg-gray-50 rounded-lg"
					>
						<p
							class="text-xs font-medium text-gray-500 uppercase tracking-wide"
						>
							Modification Date
						</p>
						<p
							class="text-sm font-semibold text-gray-800 mt-0.5 sm:mt-1"
						>
							{{
								(vehicleDetails &&
									vehicleDetails.modificationDate) ||
								'N/A'
							}}
						</p>
					</div>
				</div>
			</div>

			<!-- Action Buttons -->
			<div
				class="flex flex-col-reverse sm:flex-row justify-between gap-3 sm:gap-4 mt-6 sm:mt-8 pt-4 sm:pt-5 border-t border-gray-200"
			>
				<!-- Back Button -->
				<button
					@click="previous"
					class="flex items-center justify-center px-4 py-2.5 sm:px-5 sm:py-3 text-sm sm:text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
				>
					<i class="fas fa-arrow-left mr-1.5 sm:mr-2"></i>
					Previous Step
				</button>

				<!-- Confirm & Pay Button -->
				<button
					@click="confirmAndProceed"
					:disabled="isLoading"
					class="flex items-center justify-center px-4 py-2.5 sm:px-6 sm:py-3 text-sm sm:text-base font-medium text-white bg-primary rounded-lg hover:bg-blue-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-75 disabled:cursor-not-allowed min-w-[160px] sm:min-w-[180px]"
				>
					<!-- Loading state -->
					<span v-show="isLoading" class="flex items-center">
						<i
							class="fas fa-spinner animate-spin -ml-1 mr-1.5 sm:mr-2 h-4 w-4 text-white"
						></i>
						Processing...
					</span>

					<!-- Default state -->
					<span v-show="!isLoading" class="flex items-center">
						Confirm & Pay
						<i class="fas fa-arrow-right ml-1.5 sm:ml-2"></i>
					</span>
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { CompleteQuotation, CreateQuotationApi } from '../../../api/insurance';
import { loadState } from '../../../../../libs/persist';

export default {
	name: 'SummaryPage',
	data() {
		return {
			isLoading: false,
		};
	},
	computed: {
		...mapGetters('quotation', [
			'selectQuotationData',
			'selectQuotationResult',
			'selectCalculatedQuote',
		]),
		...mapGetters('product', ['selectedProduct', 'selectedCoverType']),
		...mapState('insurance/motorPrivate', ['vehicleValue']),
		...mapGetters('risk', ['selectRiskData']),
		...mapGetters('kyc', ['selectKycData']),
		...mapGetters('benefits', ['selectedBenefits', 'selectedExcesses']),
		...mapGetters('quotation', ['selectCalculatedQuote']),
		coreBenefits() {
			return (
				(this.selectedProduct && this.selectedProduct.coreBenefits) ||
				[]
			);
		},
		calculatedPremium() {
			if (this.selectedCoverType === 'third-party') {
				const savedBasePremium = loadState('basePremium');
				return savedBasePremium !== null &&
					savedBasePremium !== undefined
					? Number(savedBasePremium)
					: 3500;
			}
			return (
				(this.selectCalculatedQuote &&
					this.selectCalculatedQuote.total) ||
				null
			);
		},
		vehicleDetails() {
			return this.selectRiskData;
		},
		quotation() {
			return this.selectQuotationData;
		},
		kyc() {
			return this.selectKycData;
		},
		excessValuee() {
			let value = 0;
			if (this.selectedExcesses.length > 0) {
				value = this.selectedExcesses[0].value;
			}
			return value;
		},
	},
	methods: {
		...mapActions('quotation', ['setSystemQuotationRef', 'setInvoiceRef']),
		startLoading() {
			this.isLoading = true;
		},
		stopLoading() {
			this.isLoading = false;
		},
		getPoliyData() {
			const benefitStruct = this.selectedBenefits.map((ben) => {
				const benefitSystemRef = ben.benefit.benefitSystemRef;
				const benefitInternaleRef = ben.benefit.benefitInternaleRef;
				const params = {
					limit: ben.limit,
				};
				return { benefitSystemRef, benefitInternaleRef, params };
			});

			return {
				risks: [
					{
						riskType: 'ASSET',
						value:
							this.selectedCoverType === 'third-party'
								? null
								: (this.quotation &&
										this.quotation.vehicleValue) ||
									this.vehicleValue,
						riskRef:
							this.vehicleDetails &&
							this.vehicleDetails.registrationNumber,
						details: {},
					},
				],
				bussinessType: 'DIRECT',
				productSystemRef:
					this.selectedProduct &&
					this.selectedProduct.productSystemRef,
				clientDetails: {
					email: this.kyc && this.kyc.email,
					kraPin: this.kyc && this.kyc.kraPin,
					names: {
						fname: this.kyc.names.firstName || '',
						lname: this.kyc.names.lastName || '',
					},
					kyc: {},
				},
				coverPeriod:
					Number(this.quotation && this.quotation.period) || 365,
				quotationChannel: 'web',
				benefits: [...benefitStruct],
				excessValue: this.excessValuee,
			};
		},
		previous() {
			if (this.selectKycData.kraPin == 'N/A') {
				this.$router.push({ name: 'KYCDetailsForm' });
			} else {
				this.$router.push({ name: 'VehicleDetailsForm' });
			}
		},
		async confirmAndProceed() {
			if (!this.selectedProduct || !this.vehicleDetails || !this.kyc) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Incomplete Data',
					message: 'Please ensure all required details are provided.',
					duration: 3000,
				});
				return;
			}
			const benefitStruct = this.selectedBenefits.map((ben) => {
				const benefitSystemRef = ben.benefit.benefitSystemRef;
				const benefitInternaleRef = ben.benefit.benefitInternaleRef;
				const params = {
					limit: ben.limit,
				};
				return { benefitSystemRef, benefitInternaleRef, params };
			});

			this.startLoading();
			try {
				const res = await CreateQuotationApi(this.getPoliyData());
				if (res.status) {
					console.log('===benefitsStruct', benefitStruct);
					const response = await CompleteQuotation({
						systemQuotationRef:
							res.data.result.data.systemQuotationRef,
						// benefits:[
						// 	...benefitStruct
						// ]
					});
					if (response.status) {
						this.setInvoiceRef(response.data.invoiceSystemRef);
						console.log(
							'invoiceref',
							response.data.invoiceSystemRef,
						);
						this.$emit('summary-confirmed', {
							selectedBenefits: this.coreBenefits,
							totalPremium: this.calculatedPremium,
						});
						this.$router.push({ name: 'Payment' });
						Event.$emit('notifications:show', {
							type: 'success',
							title: 'Quotation Completed',
							message:
								'Your quotation has been successfully completed.',
							duration: 3000,
						});
					} else {
						throw new Error('Failed to complete quotation.');
					}
				} else {
					throw new Error('Failed to create quotation.');
				}
			} catch (error) {
				console.log('errorMesage', error);
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Quotation Failed',
					message:
						error.message ||
						'Failed to create quotation. Please try again.',
					duration: 3000,
				});
			} finally {
				this.stopLoading();
			}
		},
	},
	created() {
		console.log('SummaryPage created with state:', {
			selectedCoverType: this.selectedCoverType,
			selectedProduct: this.selectedProduct,
			vehicleDetails: this.vehicleDetails,
			kycDetails: this.kyc,
			coreBenefits: this.coreBenefits,
		});
	},
	mounted() {
		console.log('Quotation:', this.quotation);
	},
};
</script>

<style scoped>
@keyframes fade-in-up {
	from {
		opacity: 0;
		transform: translateY(10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}
.animate-fade-in-up {
	animation: fade-in-up 0.4s ease-out;
}

/* Improved responsive design */
@media (max-width: 640px) {
	/* Better text wrapping for small screens */
	.text-sm {
		word-break: break-word;
	}

	/* Improved spacing for mobile */
	.space-y-4 > * + * {
		margin-top: 1rem;
	}
}

/* Additional breakpoint for very small devices */
@media (max-width: 360px) {
	.p-3 {
		padding: 0.6rem;
	}

	/* More compact button text */
	button .flex span {
		font-size: 0.875rem;
	}

	/* Adjust icon sizes */
	button svg {
		width: 0.875rem;
		height: 0.875rem;
	}
}
</style>
